import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Header } from '@/components/Header';
import { useTeamUsage } from '@/hooks/useTeamUsage';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { useQueryClient } from '@tanstack/react-query';
import { useImpersonation } from '@/contexts/ImpersonationContext';

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { planLimits, loading, refreshUsage, subscription } = useTeamUsage();
  const { getEffectiveUserId } = useImpersonation();
  const [checking, setChecking] = useState(true);
  const [initialPlanTier, setInitialPlanTier] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 15; // Poll for up to 15 seconds

  // Store initial plan tier when component mounts
  useEffect(() => {
    if (subscription?.plan_tier && !initialPlanTier) {
      setInitialPlanTier(subscription.plan_tier);
    }
  }, [subscription?.plan_tier, initialPlanTier]);

  useEffect(() => {
    let pollInterval: NodeJS.Timeout | null = null;
    let mounted = true;
    let hasSynced = false;

    const checkSubscription = async () => {
      if (!mounted) return;
      
      setAttempts(prev => {
        const newAttempts = prev + 1;
        
        // On first attempt, sync from Polar to ensure subscription is created
        if (newAttempts === 1 && !hasSynced) {
          hasSynced = true;
          logger.debug('Syncing subscription from Polar after checkout...');
          // Get effective user ID (impersonated or actual)
          // Note: During checkout, we always want the actual user, not impersonated
          getEffectiveUserId().then((effectiveUserId) => {
            return supabase.functions.invoke('sync-polar-subscription', {
              body: { effectiveUserId },
            });
          }).then((result) => {
              if (!result.error) {
                logger.debug('Sync from Polar completed');
                // Wait a moment for DB to update, then refresh
                setTimeout(() => {
                  refreshUsage();
                }, 1000);
              } else {
                logger.debug('Sync from Polar failed (may not have subscription yet):', result.error);
                refreshUsage();
              }
            })
            .catch((error) => {
              logger.error('Error syncing from Polar:', error);
              refreshUsage();
            });
        } else {
          // Refresh data on subsequent attempts
          refreshUsage().then(() => {
            // After refresh, check if plan has changed
            // We'll check in the next render cycle via the subscription dependency
            if (newAttempts >= maxAttempts) {
              setChecking(false);
              if (pollInterval) clearInterval(pollInterval);
              // If we've exhausted attempts and still checking, redirect anyway
              // The subscription might be updated but we couldn't detect it
              if (mounted) {
                setTimeout(() => {
                  navigate('/dashboard');
                }, 1000);
              }
            }
          });
        }
        
        return newAttempts;
      });
    };

    // Start polling immediately, then continue every second
    // Webhook processing can take 1-3 seconds, so we start checking right away
    if (mounted) {
      refreshUsage();
      pollInterval = setInterval(checkSubscription, 1000);
    }

    return () => {
      mounted = false;
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [refreshUsage, navigate]);

  // Check if subscription was created/updated and redirect to dashboard
  useEffect(() => {
    if (subscription && checking) {
      // For new accounts: if subscription has polar_subscription_id, checkout succeeded - redirect
      // This handles first-time checkout where user had no subscription before
      const isNewSubscription = !initialPlanTier && subscription.polar_subscription_id && 
                                 (subscription.status === 'active' || subscription.status === 'trialing');
      
      // For new accounts: if we started with 'starter' or null and now have a paid plan, redirect
      const isNewAccountUpgrade = (!initialPlanTier || initialPlanTier === 'starter') && 
                                   subscription.plan_tier !== 'starter' &&
                                   subscription.plan_tier !== initialPlanTier;
      
      // For existing accounts: if plan tier changed, redirect
      const isPlanChanged = initialPlanTier && 
                           subscription.plan_tier !== initialPlanTier;
      
      if ((isNewSubscription || isNewAccountUpgrade || isPlanChanged) && checking) {
        // Subscription is created/updated! Redirect to dashboard
        setChecking(false);
        
        // Invalidate query cache to force fresh data fetch on dashboard
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ['team-usage'] }),
          queryClient.invalidateQueries({ queryKey: ['dashboard-paginated'] }),
          queryClient.invalidateQueries({ queryKey: ['dashboard-redirect-check'] }),
        ]).then(() => {
        // Wait a moment for database to fully sync, then redirect to dashboard
        // Add a query parameter to indicate we just completed checkout
        setTimeout(() => {
          navigate('/dashboard?checkout=success');
        }, 500);
        });
      }
    }
  }, [subscription, initialPlanTier, checking, navigate, queryClient]);

  const sessionId = searchParams.get('session_id');

  if (!sessionId) {
    navigate('/settings?tab=billing');
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-6 py-16 max-w-2xl">
        <Card className="border-primary/20">
          <CardHeader className="text-center space-y-4">
            {checking || loading ? (
              <>
                <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 text-primary animate-spin" />
                </div>
                <CardTitle className="text-2xl">Processing Your Subscription</CardTitle>
                <CardDescription>
                  Please wait while we activate your new plan...
                </CardDescription>
              </>
            ) : (
              <>
                <div className="mx-auto w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
                <CardTitle className="text-2xl">Payment Successful!</CardTitle>
                <CardDescription>
                  Your subscription has been activated
                </CardDescription>
              </>
            )}
          </CardHeader>
          
          {!checking && !loading && planLimits && (
            <CardContent className="space-y-6">
              <div className="bg-muted/50 rounded-lg p-6 space-y-2">
                <h3 className="font-semibold text-lg capitalize">
                  {planLimits.plan_tier} Plan
                </h3>
                <div className="space-y-1 text-sm text-muted-foreground">
                  <p>• Up to {planLimits.max_active_norths} active North Reports</p>
                </div>
                {subscription?.plan_tier !== planLimits.plan_tier && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                    ⚠️ Plan is still updating. If this doesn't change, please refresh or check your billing settings.
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-3">
                <Button 
                  onClick={async () => {
                    setChecking(true);
                    await refreshUsage();
                    setTimeout(() => setChecking(false), 1000);
                  }}
                  variant="outline"
                  className="w-full"
                >
                  Refresh Subscription Status
                </Button>
                <Button 
                  onClick={() => navigate('/settings?tab=billing')}
                  className="w-full"
                >
                  View Billing Dashboard
                </Button>
                <Button 
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  className="w-full"
                >
                  Go to Dashboard
                </Button>
              </div>
            </CardContent>
          )}
        </Card>
      </main>
    </div>
  );
}
